<template>
  <section>
    <form-base :teleport-target="dialogTeleportTarget" hide-required-legend>
      <template #form>
        <v-card elevation="0" class="my-6" v-if="warehouses?.length === 0">
          <v-card-title>
            <p class="typography-body-md-bold text-center">
              <v-icon size="small" class="mr-2">mdi-alert</v-icon>
              No warehouses found for this org
            </p>
          </v-card-title>
        </v-card>
        <v-form
          ref="form"
          v-on:submit.prevent
          @keyup.enter="toggleForAllWarehouses"
          v-else
          class="d-flex flex-column gap-4">
          <v-select
            label="Features"
            placeholder="Select a feature to enable/disable"
            :items="featuresItems"
            v-model="selectedFeature"
            item-title="text"
            item-value="value" />

          <p class="typography-body-md-bold">
            {{ selectedFeatureDescription }}
          </p>

          <div class="border rounded-lg pa-2 mt-4" v-if="selectedFeature && warehouses.length > 0">
            <v-list style="max-height: 400px">
              <template v-slot:activator>
                <v-list-item-title><strong>Warehouses feature status</strong></v-list-item-title>
                <v-list-item-action>({{ warehouses.length }})</v-list-item-action>
              </template>
              <v-list-item>
                <v-row>
                  <v-col align-self="start">
                    <strong>Warehouse</strong>
                  </v-col>
                  <v-col class="text-center">
                    <strong>Is Enabled?</strong>
                  </v-col>
                  <v-col class="text-center">
                    <strong>Coordinates set?</strong>
                  </v-col>
                </v-row>
              </v-list-item>
              <v-list-item v-for="warehouse of warehouses" :key="warehouse.id">
                <v-row>
                  <v-col>
                    {{ warehouse.name }}
                  </v-col>
                  <v-col class="text-center">
                    <v-btn icon variant="flat" @click="selectedWarehouseIdToggle = warehouse.id">
                      <v-icon
                        size="large"
                        :color="
                          isFeatureInstalled(warehouse.id, selectedFeature) ? 'primary' : 'grey'
                        ">
                        {{
                          isFeatureInstalled(warehouse.id, selectedFeature)
                            ? 'mdi-toggle-switch'
                            : 'mdi-toggle-switch-off'
                        }}
                      </v-icon>
                    </v-btn>
                  </v-col>
                  <v-col class="text-center">
                    <v-btn
                      variant="flat"
                      icon
                      @click="toggleGeolocation(warehouse, selectedFeature)">
                      <v-icon
                        size="large"
                        :title="isGeolocationSet(warehouse.id) ? 'Yes' : 'No'"
                        :color="isGeolocationSet(warehouse.id) ? 'primary' : 'grey'">
                        {{
                          isGeolocationSet(warehouse.id) ? 'mdi-map-marker' : 'mdi-map-marker-off'
                        }}
                      </v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-list-item>
            </v-list>
          </div>
        </v-form>
      </template>
      <template #form-actions>
        <v-card-actions class="full-width px-4">
          <template v-if="warehouses.length > 0 && selectedFeature">
            <v-btn variant="outlined" @click="() => toggleForAllWarehouses(false)">
              Disable for all
            </v-btn>
            <v-btn variant="outlined" @click="() => toggleForAllWarehouses(true)">
              Enable for all
            </v-btn>
            <v-btn
              variant="outlined"
              @click="installForms"
              v-if="isInstallFormsVisible"
              title="Ensure the Custom Forms are installed for all warehouses. Click only if the form settings are still not visible after enabling the feature.">
              <v-icon size="small" class="mr-2">mdi-information</v-icon>
              Install Forms
            </v-btn>
          </template>
          <v-spacer />
          <v-btn @click="cancel">Close</v-btn>
        </v-card-actions>
      </template>
    </form-base>
    <v-dialog
      :model-value="Boolean(selectedWarehouseIdToggle)"
      width="700"
      v-if="selectedWarehouseIdToggle">
      <v-card>
        <v-card-title class="bg-primary">
          <span class="ml-2 text--color-white">Confirm {{ toggleAction }} feature?</span>
        </v-card-title>
        <v-card-text>
          <p class="my-4 font-size-medium">
            Are you sure to
            <strong class="text-uppercase">{{ toggleAction }}</strong>
            the
            <strong>{{ selectedFeatureTitle }}</strong>
            feature for the warehouse
            <strong>"{{ warehouses.find(w => w.id === selectedWarehouseIdToggle)?.name }}"</strong>
            ?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="outlined" @click="selectedWarehouseIdToggle = null">Nevermind</v-btn>
          <v-btn variant="text" class="bg-primary" @click="toggleFeature">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <edit-org-warehouse-geolocation-dialog
      v-if="editingWarehouse"
      external-activator
      :warehouse="editingWarehouse"
      @close="editingWarehouse = null"
      @updated="getWarehouses"
      :show-dialog="Boolean(editingWarehouse?.id)" />
  </section>
</template>

<script>
import { defineComponent, ref, computed, inject, onBeforeMount } from 'vue';
import { isFormValid } from '@satellite/plugins/util.js';
import { isUUID, isDefined } from 'class-validator';
import {
  CustomFormsFeaturesData,
  CustomFormsFeatures,
  WarehouseFeatureEnum,
  isFeatureEnabledForWarehouse
} from '@nova/core';
import EditOrgWarehouseGeolocationDialog from '@/modules/orgs/components/EditOrgWarehouseGeolocationDialog.vue';
import { useNotify } from '@/composables';

export default defineComponent({
  props: {
    orgId: {
      type: String,
      required: true
    },
    orgName: {
      type: String,
      required: true
    }
  },
  components: {
    EditOrgWarehouseGeolocationDialog
  },
  emits: ['loading', 'close'],
  setup(props, { emit }) {
    const notify = useNotify();
    const dialogTeleportTarget = inject('dialogActionsRef');

    const form = ref(null);
    const warehouses = ref([]);
    const selectedFeature = ref(WarehouseFeatureEnum.GateManagement);
    const selectedWarehouseIdToggle = ref(null);
    const editingWarehouse = ref(null);

    const featuresItems = [
      {
        text: CustomFormsFeaturesData[WarehouseFeatureEnum.GateManagement].title,
        value: WarehouseFeatureEnum.GateManagement
      },
      {
        text: CustomFormsFeaturesData[WarehouseFeatureEnum.YMS].title,
        value: WarehouseFeatureEnum.YMS
      },
      {
        text: CustomFormsFeaturesData[WarehouseFeatureEnum.NavTrac].title,
        value: WarehouseFeatureEnum.NavTrac
      }
    ];

    const selectedFeatureDescription = computed(
      () => CustomFormsFeaturesData[selectedFeature.value]?.description
    );
    const selectedFeatureTitle = computed(
      () => CustomFormsFeaturesData[selectedFeature.value]?.title
    );

    const findWarehouse = warehouseId => {
      return warehouses.value.find(w => w.id === warehouseId) ?? {};
    };

    const toggleAction = computed(() => {
      if (!(selectedWarehouseIdToggle.value && selectedFeature.value)) {
        return;
      }
      return isFeatureInstalled(selectedWarehouseIdToggle.value, selectedFeature.value)
        ? 'disable'
        : 'enable';
    });

    const isGeolocationSet = warehouseId => {
      const geolocation = findWarehouse(warehouseId).geolocation;
      return isDefined(geolocation?.latitude) && isDefined(geolocation?.longitude);
    };

    const isFeatureInstalled = (warehouseId, feature) => {
      return isFeatureEnabledForWarehouse(findWarehouse(warehouseId), feature);
    };

    const isInstallFormsVisible = computed(() => {
      return [
        WarehouseFeatureEnum.GateManagement,
        WarehouseFeatureEnum.YMS,
        WarehouseFeatureEnum.NavTrac
      ].includes(selectedFeature.value);
    });

    const toggleFeature = async () => {
      if (!isUUID(selectedWarehouseIdToggle.value)) {
        notify('Invalid warehouse', 'error');
        return;
      }

      emit('loading', true);
      try {
        let response = {};

        const url = `/org/${props.orgId}/toggle-warehouse-feature`;
        response = await axios.post(url, {
          warehouseIds: [selectedWarehouseIdToggle.value],
          featureCode: selectedFeature.value
        });

        if (response?.data?.ok === true) {
          notify(
            `${selectedFeatureTitle.value} was ${toggleAction.value}d for the warehouse ${
              findWarehouse(selectedWarehouseIdToggle.value).name
            } at org ${props.orgName}`,
            'success'
          );
          await getWarehouses();
        } else {
          throw new Error(response?.data?.message || 'Unknown error');
        }
      } finally {
        emit('loading', false);
        selectedWarehouseIdToggle.value = null;
      }
    };

    const toggleGeolocation = warehouse => {
      editingWarehouse.value = warehouse;
    };

    const toggleForAllWarehouses = async (enableForAll = true) => {
      if (!(await isFormValid(form.value))) {
        return;
      }

      const action = enableForAll ? 'enable' : 'disable';

      emit('loading', true);
      try {
        const response = await axios.post(`/org/${props.orgId}/toggle-warehouse-feature`, {
          warehouseIds: warehouses.value.map(w => w.id),
          featureCode: selectedFeature.value,
          forceOperation: action
        });

        if (response?.data?.ok === true) {
          notify(
            `${selectedFeatureTitle.value} was ${action}d at ${props.orgName} for all warehouses`,
            'success'
          );
          await getWarehouses();
        } else {
          throw new Error(response?.data?.message || 'Unknown error');
        }
      } catch (error) {
        notify('Error enabling feature for all warehouses', 'error');
      } finally {
        emit('loading', false);
        selectedWarehouseIdToggle.value = null;
      }
    };

    const getWarehouses = async () => {
      emit('loading', true);

      try {
        const response = await axios.get('/warehouse', {
          params: {
            s: { orgId: props.orgId, isActive: true },
            limit: 1000
          }
        });

        warehouses.value = response?.data?.data || [];
      } catch (error) {
        notify('Error fetching warehouses', 'error');
      } finally {
        emit('loading', false);
      }
    };

    const installForms = async () => {
      emit('loading', true);
      try {
        let feature = '';

        if (selectedFeature.value === WarehouseFeatureEnum.GateManagement) {
          feature = CustomFormsFeatures.CheckInAdditionalAssetDetails;
        }

        if (selectedFeature.value === WarehouseFeatureEnum.YMS) {
          feature = CustomFormsFeatures.AssetContainerDetails;
        }

        if (!feature) {
          notify('Invalid feature selected', 'error');
          return;
        }

        const response = await axios.post(`/org/${props.orgId}/features/${feature}`, {
          warehouseIds: warehouses.value.map(w => w.id)
        });

        if (response?.data?.ok === true) {
          notify(`Custom Forms were installed for all warehouses at ${props.orgName}`, 'success');
          await getWarehouses();
        } else {
          throw new Error(response?.data?.message || 'Unknown error');
        }
      } catch (error) {
        notify('Error installing forms for all warehouses', 'error');
      } finally {
        emit('loading', false);
      }
    };

    onBeforeMount(() => getWarehouses());

    return {
      form,
      dialogTeleportTarget,
      selectedFeature,
      selectedWarehouseIdToggle,
      warehouses,
      editingWarehouse,
      featuresItems,
      selectedFeatureDescription,
      selectedFeatureTitle,
      toggleAction,
      isInstallFormsVisible,
      installForms,
      getWarehouses,
      findWarehouse,
      isGeolocationSet,
      isFeatureInstalled,
      toggleFeature,
      toggleGeolocation,
      toggleForAllWarehouses,
      cancel: () => emit('close')
    };
  }
});
</script>
